import type { RegionDefinition, RegionId } from "./regionTypes";

/**
 * The 14 Overworld regions, in chronological/journey order.
 * `connectsTo` is a simple linear chain — each region links to its
 * immediate neighbor(s) — giving a single continuous road.
 */
export const OVERWORLD_REGIONS: readonly RegionDefinition[] = [
  {
    id: "creation",
    order: 1,
    titleKey: "overworld.region.creation.name",
    taglineKey: "overworld.region.creation.tagline",
    connectsTo: ["garden-of-eden"],
  },
  {
    id: "garden-of-eden",
    order: 2,
    titleKey: "overworld.region.gardenOfEden.name",
    taglineKey: "overworld.region.gardenOfEden.tagline",
    connectsTo: ["creation", "the-fallen-world"],
  },
  {
    id: "the-fallen-world",
    order: 3,
    titleKey: "overworld.region.theFallenWorld.name",
    taglineKey: "overworld.region.theFallenWorld.tagline",
    connectsTo: ["garden-of-eden", "the-flood"],
  },
  {
    id: "the-flood",
    order: 4,
    titleKey: "overworld.region.theFlood.name",
    taglineKey: "overworld.region.theFlood.tagline",
    connectsTo: ["the-fallen-world", "tower-of-babel"],
  },
  {
    id: "tower-of-babel",
    order: 5,
    titleKey: "overworld.region.towerOfBabel.name",
    taglineKey: "overworld.region.towerOfBabel.tagline",
    connectsTo: ["the-flood", "patriarchs"],
  },
  {
    id: "patriarchs",
    order: 6,
    titleKey: "overworld.region.patriarchs.name",
    taglineKey: "overworld.region.patriarchs.tagline",
    connectsTo: ["tower-of-babel", "egypt"],
  },
  {
    id: "egypt",
    order: 7,
    titleKey: "overworld.region.egypt.name",
    taglineKey: "overworld.region.egypt.tagline",
    connectsTo: ["patriarchs", "wilderness"],
  },
  {
    id: "wilderness",
    order: 8,
    titleKey: "overworld.region.wilderness.name",
    taglineKey: "overworld.region.wilderness.tagline",
    connectsTo: ["egypt", "promised-land"],
  },
  {
    id: "promised-land",
    order: 9,
    titleKey: "overworld.region.promisedLand.name",
    taglineKey: "overworld.region.promisedLand.tagline",
    connectsTo: ["wilderness", "kingdom"],
  },
  {
    id: "kingdom",
    order: 10,
    titleKey: "overworld.region.kingdom.name",
    taglineKey: "overworld.region.kingdom.tagline",
    connectsTo: ["promised-land", "exile"],
  },
  {
    id: "exile",
    order: 11,
    titleKey: "overworld.region.exile.name",
    taglineKey: "overworld.region.exile.tagline",
    connectsTo: ["kingdom", "life-of-jesus"],
  },
  {
    id: "life-of-jesus",
    order: 12,
    titleKey: "overworld.region.lifeOfJesus.name",
    taglineKey: "overworld.region.lifeOfJesus.tagline",
    connectsTo: ["exile", "early-church"],
  },
  {
    id: "early-church",
    order: 13,
    titleKey: "overworld.region.earlyChurch.name",
    taglineKey: "overworld.region.earlyChurch.tagline",
    connectsTo: ["life-of-jesus", "revelation"],
  },
  {
    id: "revelation",
    order: 14,
    titleKey: "overworld.region.revelation.name",
    taglineKey: "overworld.region.revelation.tagline",
    connectsTo: ["early-church"],
  },
];

export function getRegionById(id: RegionId): RegionDefinition {
  const region = OVERWORLD_REGIONS.find((candidate) => candidate.id === id);
  if (!region) throw new Error(`Unknown overworld region id: ${id}`);
  return region;
}

/** Runtime type guard — validates an unknown value (e.g. a router search param) as a RegionId. */
export function isRegionId(value: unknown): value is RegionId {
  return typeof value === "string" && OVERWORLD_REGIONS.some((region) => region.id === value);
}
